import React, { useState } from 'react';
import { Plug, Database, Webhook, Activity, Settings, Zap, CheckCircle2, AlertCircle, XCircle, Shield, RefreshCw, Copy, Eye, EyeOff, ExternalLink, ChevronRight, Info, Lock, Layers, Cable, Target } from 'lucide-react';
type SubTab = 'home' | 'connection' | 'mapping' | 'kpi' | 'webhooks' | 'health';
export function IntegrationsTab() {
  const [subTab, setSubTab] = useState<SubTab>('home');
  return <div className="space-y-8 animate-in fade-in duration-500 pb-24" data-id="element-555">
      <div className="flex items-start justify-between" data-id="element-556">
        <div data-id="element-557">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-558">
            Resort Settings
          </p>
          <h1 className="font-serif text-3xl text-stone-900 mb-2" data-id="element-559">
            Integrations
          </h1>
          <p className="text-stone-500 max-w-2xl" data-id="element-560">
            Connect BlockQuote to your CRM and downstream tools. Salesforce-safe
            by design — we sync the minimum, log everything, and never own
            pricing logic outside BlockQuote.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-emerald-200 bg-emerald-50 font-mono text-xs uppercase tracking-widest text-emerald-700" data-id="element-561">
          <Shield className="w-3.5 h-3.5" data-id="element-562" />
          Salesforce-safe
        </div>
      </div>

      {/* Sub-nav */}
      <div className="border-b border-stone-200 flex items-center gap-1 overflow-x-auto" data-id="element-563">
        {[{
        id: 'home',
        label: 'Integrations Home',
        icon: Plug
      }, {
        id: 'connection',
        label: 'Salesforce Connection',
        icon: Cable
      }, {
        id: 'mapping',
        label: 'Mapping',
        icon: Layers
      }, {
        id: 'kpi',
        label: 'KPI & SLA',
        icon: Target
      }, {
        id: 'webhooks',
        label: 'Webhooks',
        icon: Webhook
      }, {
        id: 'health',
        label: 'Health & Logs',
        icon: Activity
      }].map(t => {
        const Icon = t.icon;
        const active = subTab === t.id;
        return <button key={t.id} onClick={() => setSubTab(t.id as SubTab)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${active ? 'border-red-600 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-900'}`} data-id="element-564">
              <Icon className={`w-4 h-4 ${active ? 'text-red-600' : 'text-stone-400'}`} data-id="element-565" />
              {t.label}
            </button>;
      })}
      </div>

      {subTab === 'home' && <IntegrationsHome onPick={id => setSubTab(id)} data-id="element-566" />}
      {subTab === 'connection' && <SalesforceConnection data-id="element-567" />}
      {subTab === 'mapping' && <SalesforceMapping data-id="element-568" />}
      {subTab === 'kpi' && <KpiSync data-id="element-569" />}
      {subTab === 'webhooks' && <Webhooks data-id="element-570" />}
      {subTab === 'health' && <Health data-id="element-571" />}
    </div>;
}
// ────────────────────────────────────────────────
// A) Integrations Home
// ────────────────────────────────────────────────
function IntegrationsHome({
  onPick
}: {
  onPick: (id: SubTab) => void;
}) {
  return <div className="space-y-6" data-id="element-572">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-573">
        {/* Salesforce */}
        <button onClick={() => onPick('connection')} className="text-left bg-white border border-stone-200 hover:border-stone-300 shadow-sm p-6 transition-colors group" data-id="element-574">
          <div className="flex items-start justify-between mb-5" data-id="element-575">
            <div className="w-12 h-12 bg-blue-50 border border-blue-100 flex items-center justify-center" data-id="element-576">
              <Cable className="w-5 h-5 text-blue-600" data-id="element-577" />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1" data-id="element-578">
              Recommended
            </span>
          </div>
          <h3 className="font-serif text-xl text-stone-900 mb-2" data-id="element-579">Salesforce</h3>
          <p className="text-sm text-stone-500 mb-5 leading-relaxed" data-id="element-580">
            Sync Deal ID, status, timestamps, and a deep link back to the
            Opportunity. No schema changes required.
          </p>
          <div className="flex items-center justify-between pt-4 border-t border-stone-100" data-id="element-581">
            <div className="flex items-center gap-2" data-id="element-582">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" data-id="element-583" />
              <span className="font-mono text-xs text-stone-700" data-id="element-584">
                Connected
              </span>
            </div>
            <span className="font-mono text-xs text-stone-400" data-id="element-585">
              Last sync 2m ago
            </span>
          </div>
          <div className="flex items-center justify-end gap-1 mt-4 text-sm font-medium text-red-600 group-hover:gap-2 transition-all" data-id="element-586">
            Configure <ChevronRight className="w-4 h-4" data-id="element-587" />
          </div>
        </button>

        {/* Other CRM */}
        <button onClick={() => onPick('webhooks')} className="text-left bg-white border border-stone-200 hover:border-stone-300 shadow-sm p-6 transition-colors group" data-id="element-588">
          <div className="flex items-start justify-between mb-5" data-id="element-589">
            <div className="w-12 h-12 bg-stone-50 border border-stone-100 flex items-center justify-center" data-id="element-590">
              <Webhook className="w-5 h-5 text-stone-700" data-id="element-591" />
            </div>
          </div>
          <h3 className="font-serif text-xl text-stone-900 mb-2" data-id="element-592">Other CRM</h3>
          <p className="text-sm text-stone-500 mb-5 leading-relaxed" data-id="element-593">
            Generic webhook delivery for HubSpot, Pipedrive, or any system that
            accepts JSON over HTTPS.
          </p>
          <div className="flex items-center justify-between pt-4 border-t border-stone-100" data-id="element-594">
            <div className="flex items-center gap-2" data-id="element-595">
              <div className="w-2 h-2 bg-stone-300 rounded-full" data-id="element-596" />
              <span className="font-mono text-xs text-stone-500" data-id="element-597">
                Not connected
              </span>
            </div>
            <span className="font-mono text-xs text-stone-400" data-id="element-598">—</span>
          </div>
          <div className="flex items-center justify-end gap-1 mt-4 text-sm font-medium text-stone-700 group-hover:gap-2 transition-all" data-id="element-599">
            Set up <ChevronRight className="w-4 h-4" data-id="element-600" />
          </div>
        </button>

        {/* Zapier */}
        <div className="bg-stone-50 border border-stone-200 border-dashed p-6 opacity-80" data-id="element-601">
          <div className="flex items-start justify-between mb-5" data-id="element-602">
            <div className="w-12 h-12 bg-white border border-stone-200 flex items-center justify-center" data-id="element-603">
              <Zap className="w-5 h-5 text-amber-600" data-id="element-604" />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-stone-500 bg-white border border-stone-200 px-2 py-1" data-id="element-605">
              Coming soon
            </span>
          </div>
          <h3 className="font-serif text-xl text-stone-900 mb-2" data-id="element-606">
            Zapier / Make
          </h3>
          <p className="text-sm text-stone-500 mb-5 leading-relaxed" data-id="element-607">
            Pre-built triggers and actions for low-code automation. Available in
            a future release.
          </p>
          <div className="flex items-center justify-between pt-4 border-t border-stone-200" data-id="element-608">
            <div className="flex items-center gap-2" data-id="element-609">
              <div className="w-2 h-2 bg-stone-300 rounded-full" data-id="element-610" />
              <span className="font-mono text-xs text-stone-500" data-id="element-611">
                Unavailable
              </span>
            </div>
            <span className="font-mono text-xs text-stone-400" data-id="element-612">—</span>
          </div>
        </div>
      </div>

      {/* Safety strip */}
      <div className="bg-white border border-stone-200 shadow-sm p-6" data-id="element-613">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-614">
          <div className="flex items-start gap-3" data-id="element-615">
            <Shield className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" data-id="element-616" />
            <div data-id="element-617">
              <p className="font-medium text-sm text-stone-900 mb-1" data-id="element-618">
                Salesforce-safe
              </p>
              <p className="text-xs text-stone-500 leading-relaxed" data-id="element-619">
                We sync Deal ID + status + timestamps + link. Nothing else
                without explicit toggle.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-620">
            <Lock className="w-5 h-5 text-stone-700 mt-0.5 flex-shrink-0" data-id="element-621" />
            <div data-id="element-622">
              <p className="font-medium text-sm text-stone-900 mb-1" data-id="element-623">
                No CRM replacement
              </p>
              <p className="text-xs text-stone-500 leading-relaxed" data-id="element-624">
                No pricing logic in Salesforce. Your CRM stays system-of-record.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-625">
            <Activity className="w-5 h-5 text-stone-700 mt-0.5 flex-shrink-0" data-id="element-626" />
            <div data-id="element-627">
              <p className="font-medium text-sm text-stone-900 mb-1" data-id="element-628">
                Health + audit logs
              </p>
              <p className="text-xs text-stone-500 leading-relaxed" data-id="element-629">
                Every sync event logged with user, timestamp, and diff.
                Retryable and reviewable.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ────────────────────────────────────────────────
// B) Salesforce Connection
// ────────────────────────────────────────────────
function SalesforceConnection() {
  const [connected] = useState(true);
  return <div className="space-y-6" data-id="element-630">
      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-631">
        <div className="p-6 border-b border-stone-100 flex items-start justify-between gap-4" data-id="element-632">
          <div data-id="element-633">
            <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-634">
              Salesforce Connection
            </h2>
            <p className="text-sm text-stone-500" data-id="element-635">
              OAuth-based connection via Salesforce Connected App. Tokens are
              encrypted at rest.
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-100" data-id="element-636">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-637" />
            <span className="font-mono text-xs uppercase tracking-widest text-emerald-700" data-id="element-638">
              Connected
            </span>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-id="element-639">
          <Field label="Connected Org" value="Lomas Travel Group" data-id="element-640" />
          <Field label="Org ID" value="00D5g000004XyZaEAK" mono data-id="element-641" />
          <Field label="Instance URL" value="https://lomas.my.salesforce.com" mono data-id="element-642" />
          <Field label="API Version" value="v60.0" mono data-id="element-643" />
          <Field label="Authorized By" value="vicente.gomez@lomas.com" data-id="element-644" />
          <Field label="Authorized On" value="Mar 18, 2026 · 09:42" data-id="element-645" />
        </div>

        <div className="p-6 border-t border-stone-100 flex flex-col sm:flex-row gap-3" data-id="element-646">
          {connected ? <>
              <button className="px-4 py-2 border border-stone-300 bg-white hover:bg-stone-50 text-sm font-medium text-stone-700 transition-colors inline-flex items-center gap-2" data-id="element-647">
                <RefreshCw className="w-4 h-4" data-id="element-648" /> Test connection
              </button>
              <button className="px-4 py-2 border border-red-200 bg-white hover:bg-red-50 text-sm font-medium text-red-700 transition-colors" data-id="element-649">
                Disconnect
              </button>
            </> : <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors inline-flex items-center gap-2" data-id="element-650">
              Connect Salesforce <ExternalLink className="w-4 h-4" data-id="element-651" />
            </button>}
        </div>
      </div>

      {/* Permission scope */}
      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-652">
        <div className="p-6 border-b border-stone-100" data-id="element-653">
          <h3 className="font-serif text-lg text-stone-900 mb-1" data-id="element-654">
            Permission Scope
          </h3>
          <p className="text-sm text-stone-500" data-id="element-655">
            BlockQuote only writes Deal status, timestamps, and a deep link
            back. No pricing data leaves your CRM.
          </p>
        </div>
        <div className="divide-y divide-stone-100" data-id="element-656">
          {[{
          scope: 'api',
          desc: 'Access standard objects via the REST API',
          granted: true
        }, {
          scope: 'refresh_token, offline_access',
          desc: 'Maintain background sync without re-auth',
          granted: true
        }, {
          scope: 'Opportunity (read + write to BlockQuote_* fields)',
          desc: 'Scoped to namespaced fields only',
          granted: true
        }, {
          scope: 'Contact (read only)',
          desc: 'Resolve quote requester identity',
          granted: true
        }, {
          scope: 'Account (read only, optional)',
          desc: 'Optional agency identifier lookup',
          granted: false
        }].map((s, i) => <div key={i} className="px-6 py-3 flex items-center justify-between" data-id="element-657">
              <div data-id="element-658">
                <p className="font-mono text-sm text-stone-800" data-id="element-659">{s.scope}</p>
                <p className="text-xs text-stone-500 mt-0.5" data-id="element-660">{s.desc}</p>
              </div>
              {s.granted ? <span className="inline-flex items-center gap-1.5 text-xs font-mono text-emerald-700" data-id="element-661">
                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-662" /> Granted
                </span> : <span className="inline-flex items-center gap-1.5 text-xs font-mono text-stone-400" data-id="element-663">
                  <XCircle className="w-3.5 h-3.5" data-id="element-664" /> Not granted
                </span>}
            </div>)}
        </div>
      </div>

      {/* Security callouts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-665">
        <Note title="Salesforce remains system-of-record." body="Pipeline reporting, forecasting, and account history continue to live in Salesforce. BlockQuote never claims ownership of CRM data." data-id="element-666" />
        <Note title="No pricing logic in Salesforce." body="Rate sheets, guardrails, and quote generation live in BlockQuote. We only mirror outputs (status, timestamps) back to your CRM." data-id="element-667" />
      </div>
    </div>;
}
function Field({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div data-id="element-668">
      <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1.5" data-id="element-669">
        {label}
      </p>
      <p className={`text-sm text-stone-900 ${mono ? 'font-mono' : ''}`} data-id="element-670">
        {value}
      </p>
    </div>;
}
function Note({
  title,
  body
}: {
  title: string;
  body: string;
}) {
  return <div className="bg-stone-900 text-stone-50 p-5 flex items-start gap-3" data-id="element-671">
      <Info className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-672" />
      <div data-id="element-673">
        <p className="font-medium text-sm text-stone-50 mb-1" data-id="element-674">{title}</p>
        <p className="text-xs text-stone-400 leading-relaxed" data-id="element-675">{body}</p>
      </div>
    </div>;
}
// ────────────────────────────────────────────────
// C) Salesforce Mapping
// ────────────────────────────────────────────────
function SalesforceMapping() {
  const [mode, setMode] = useState<'A' | 'B' | 'C'>('A');
  const [parent, setParent] = useState<'Opportunity' | 'Lead' | 'Custom'>('Opportunity');
  const modes = [{
    id: 'A' as const,
    title: 'Link + status sync',
    desc: 'Write BlockQuote fields onto the existing Opportunity. Fastest pilot path.',
    tag: 'Recommended',
    tagColor: 'bg-emerald-50 text-emerald-700 border-emerald-100'
  }, {
    id: 'B' as const,
    title: 'Custom Object',
    desc: 'Use a dedicated BlockQuote_Deal__c object that mirrors quote-level detail.',
    tag: 'Reporting',
    tagColor: 'bg-stone-100 text-stone-700 border-stone-200'
  }, {
    id: 'C' as const,
    title: 'Middleware',
    desc: 'MuleSoft / Workato / Boomi. Centralized integration governance.',
    tag: 'Enterprise',
    tagColor: 'bg-stone-100 text-stone-700 border-stone-200'
  }];
  const mappings = [{
    bq: 'Deal ID',
    sf: 'BlockQuote_Deal_ID__c',
    type: 'External ID',
    required: true
  }, {
    bq: 'Property',
    sf: 'BlockQuote_Property__c',
    type: 'Text(120)'
  }, {
    bq: 'Check-in',
    sf: 'BlockQuote_CheckIn__c',
    type: 'Date'
  }, {
    bq: 'Check-out',
    sf: 'BlockQuote_CheckOut__c',
    type: 'Date'
  }, {
    bq: 'Rooms Total',
    sf: 'BlockQuote_RoomsTotal__c',
    type: 'Number'
  }, {
    bq: 'Status',
    sf: 'BlockQuote_Status__c',
    type: 'Picklist'
  }, {
    bq: 'Deal Link',
    sf: 'BlockQuote_DealURL__c',
    type: 'URL'
  }, {
    bq: 'Submitted At',
    sf: 'BlockQuote_SubmittedAt__c',
    type: 'DateTime'
  }, {
    bq: 'First Quote At',
    sf: 'BlockQuote_FirstQuoteAt__c',
    type: 'DateTime'
  }, {
    bq: 'Hold Requested At',
    sf: 'BlockQuote_HoldRequestedAt__c',
    type: 'DateTime'
  }, {
    bq: 'Hold Approved At',
    sf: 'BlockQuote_HoldApprovedAt__c',
    type: 'DateTime'
  }, {
    bq: 'Contracted At',
    sf: 'BlockQuote_ContractedAt__c',
    type: 'DateTime',
    optional: true
  }, {
    bq: 'Quote Tier Selected',
    sf: 'BlockQuote_Tier__c',
    type: 'Picklist (G/B/B)',
    optional: true
  }, {
    bq: 'Exception Flag',
    sf: 'BlockQuote_Exception__c',
    type: 'Checkbox',
    optional: true
  }];
  return <div className="space-y-6" data-id="element-676">
      {/* Mode picker */}
      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-677">
        <div className="p-6 border-b border-stone-100" data-id="element-678">
          <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-679">
            Integration Mode
          </h2>
          <p className="text-sm text-stone-500" data-id="element-680">
            Pick how BlockQuote writes to Salesforce. You can change this later.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-681">
          {modes.map(m => {
          const active = mode === m.id;
          return <button key={m.id} onClick={() => setMode(m.id)} className={`text-left p-5 border-2 transition-colors ${active ? 'border-red-600 bg-red-50/30' : 'border-stone-200 bg-white hover:border-stone-300'}`} data-id="element-682">
                <div className="flex items-center justify-between mb-3" data-id="element-683">
                  <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-684">
                    Mode {m.id}
                  </span>
                  <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${m.tagColor}`} data-id="element-685">
                    {m.tag}
                  </span>
                </div>
                <h3 className="font-serif text-lg text-stone-900 mb-2" data-id="element-686">
                  {m.title}
                </h3>
                <p className="text-xs text-stone-500 leading-relaxed" data-id="element-687">
                  {m.desc}
                </p>
                <div className="mt-4 pt-3 border-t border-stone-200" data-id="element-688">
                  <span className={`text-xs font-medium ${active ? 'text-red-600' : 'text-stone-400'}`} data-id="element-689">
                    {active ? '✓ Selected' : 'Select'}
                  </span>
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Object mapping */}
      {mode !== 'C' && <div className="bg-white border border-stone-200 shadow-sm" data-id="element-690">
          <div className="p-6 border-b border-stone-100" data-id="element-691">
            <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-692">
              Parent Object
            </h2>
            <p className="text-sm text-stone-500" data-id="element-693">
              Where should BlockQuote deals attach in Salesforce?
            </p>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-694">
            {(['Opportunity', 'Lead', 'Custom'] as const).map(p => {
          const active = parent === p;
          const recommended = p === 'Opportunity';
          return <button key={p} onClick={() => setParent(p)} className={`p-4 border text-left transition-colors ${active ? 'border-red-600 bg-red-50/30' : 'border-stone-200 hover:border-stone-300'}`} data-id="element-695">
                  <div className="flex items-center justify-between mb-2" data-id="element-696">
                    <p className="font-mono text-sm text-stone-900" data-id="element-697">{p}</p>
                    {recommended && <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-700" data-id="element-698">
                        Recommended
                      </span>}
                  </div>
                  <p className="text-xs text-stone-500" data-id="element-699">
                    {p === 'Opportunity' && 'Attach to existing group Opportunities.'}
                    {p === 'Lead' && 'Create a Lead per inbound request.'}
                    {p === 'Custom' && 'Use BlockQuote_Deal__c custom object.'}
                  </p>
                </button>;
        })}
          </div>
        </div>}

      {/* Field mapping table */}
      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-700">
        <div className="p-6 border-b border-stone-100 flex items-start justify-between" data-id="element-701">
          <div data-id="element-702">
            <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-703">
              Field Mapping
            </h2>
            <p className="text-sm text-stone-500" data-id="element-704">
              Default template. Edit any row to point to a different Salesforce
              field.
            </p>
          </div>
          <button className="text-sm font-medium text-stone-700 hover:text-stone-900 inline-flex items-center gap-2" data-id="element-705">
            <RefreshCw className="w-4 h-4" data-id="element-706" /> Reset to default
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-707">
          <table className="w-full text-sm" data-id="element-708">
            <thead className="bg-stone-50 border-b border-stone-200" data-id="element-709">
              <tr data-id="element-710">
                <th className="text-left font-mono text-xs uppercase tracking-widest text-stone-500 px-6 py-3" data-id="element-711">
                  BlockQuote Field
                </th>
                <th className="text-left font-mono text-xs uppercase tracking-widest text-stone-500 px-6 py-3" data-id="element-712">
                  Salesforce Field
                </th>
                <th className="text-left font-mono text-xs uppercase tracking-widest text-stone-500 px-6 py-3" data-id="element-713">
                  Type
                </th>
                <th className="text-right font-mono text-xs uppercase tracking-widest text-stone-500 px-6 py-3" data-id="element-714">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-715">
              {mappings.map((m, i) => <tr key={i} className="hover:bg-stone-50/50" data-id="element-716">
                  <td className="px-6 py-3 text-stone-900" data-id="element-717">{m.bq}</td>
                  <td className="px-6 py-3 font-mono text-xs text-stone-700" data-id="element-718">
                    {m.sf}
                  </td>
                  <td className="px-6 py-3 font-mono text-xs text-stone-500" data-id="element-719">
                    {m.type}
                  </td>
                  <td className="px-6 py-3 text-right" data-id="element-720">
                    {m.required ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-red-50 text-red-700 border border-red-100" data-id="element-721">
                        Required
                      </span> : m.optional ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-stone-50 text-stone-500 border border-stone-200" data-id="element-722">
                        Optional
                      </span> : <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-723">
                        Active
                      </span>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Read from Salesforce */}
      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-724">
        <div className="p-6 border-b border-stone-100" data-id="element-725">
          <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-726">
            What BlockQuote reads from Salesforce
          </h2>
          <p className="text-sm text-stone-500" data-id="element-727">
            Read access is intentionally narrow. The fewer fields we touch, the
            easier the security review.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-728">
          {[{
          obj: 'Contact',
          fields: 'Name, Email',
          required: true
        }, {
          obj: 'Account',
          fields: 'Account / Agency Name',
          optional: true
        }, {
          obj: 'Opportunity',
          fields: 'Segment tag (Agency / Planner / Direct)',
          optional: true
        }].map((r, i) => <div key={i} className="border border-stone-200 p-4" data-id="element-729">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-730">
                {r.obj}
              </p>
              <p className="text-sm text-stone-900 mb-2" data-id="element-731">{r.fields}</p>
              {r.required ? <span className="text-[10px] font-mono uppercase tracking-widest text-red-700" data-id="element-732">
                  Required
                </span> : <span className="text-[10px] font-mono uppercase tracking-widest text-stone-500" data-id="element-733">
                  Optional
                </span>}
            </div>)}
        </div>
      </div>

      {/* Save bar */}
      <div className="bg-stone-900 text-stone-50 p-5 flex items-center justify-between" data-id="element-734">
        <p className="text-sm text-stone-400" data-id="element-735">
          Changes apply on next sync run. Existing deals will not be re-written
          automatically.
        </p>
        <div className="flex gap-2" data-id="element-736">
          <button className="px-4 py-2 border border-stone-700 text-sm font-medium text-stone-300 hover:bg-stone-800 transition-colors" data-id="element-737">
            Cancel
          </button>
          <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-sm font-medium text-white transition-colors" data-id="element-738">
            Save mapping
          </button>
        </div>
      </div>
    </div>;
}
// ────────────────────────────────────────────────
// D) KPI Sync
// ────────────────────────────────────────────────
function KpiSync() {
  const [slaHours, setSlaHours] = useState(4);
  const [kpiSync, setKpiSync] = useState(true);
  const [kpiFields, setKpiFields] = useState({
    time_to_first_quote_minutes: true,
    sla_met: true,
    exception_rate: true,
    funnel_stage: true
  });
  return <div className="space-y-6" data-id="element-739">
      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-740">
        <div className="p-6 border-b border-stone-100" data-id="element-741">
          <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-742">SLA Target</h2>
          <p className="text-sm text-stone-500" data-id="element-743">
            The deadline used to compute SLA-met for every in-policy quote.
          </p>
        </div>
        <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-744">
          <div className="flex items-center gap-3" data-id="element-745">
            <input type="number" value={slaHours} onChange={e => setSlaHours(Number(e.target.value))} min={1} max={48} className="w-24 px-3 py-2 border border-stone-300 font-mono text-xl text-stone-900 focus:outline-none focus:border-red-600" data-id="element-746" />
            <span className="font-mono text-sm text-stone-500 uppercase tracking-widest" data-id="element-747">
              hours
            </span>
          </div>
          <div className="flex-1 border-l border-stone-200 pl-6 hidden sm:block" data-id="element-748">
            <p className="text-sm text-stone-700 font-medium" data-id="element-749">
              In-policy quotes must ship within {slaHours} hours.
            </p>
            <p className="text-xs text-stone-500 mt-1" data-id="element-750">
              Out-of-policy quotes are excluded — they're routed for approval
              and tracked separately.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-751">
        <div className="p-6 border-b border-stone-100 flex items-start justify-between gap-4" data-id="element-752">
          <div data-id="element-753">
            <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-754">
              KPI Sync to Salesforce
            </h2>
            <p className="text-sm text-stone-500" data-id="element-755">
              Push computed KPIs to Opportunity fields so your execs can
              dashboard them in Salesforce directly.
            </p>
          </div>
          <Toggle on={kpiSync} onChange={setKpiSync} data-id="element-756" />
        </div>

        <div className={`p-6 ${!kpiSync ? 'opacity-40 pointer-events-none' : ''}`} data-id="element-757">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-758">
            Fields to compute + send
          </p>
          <div className="space-y-2" data-id="element-759">
            {[{
            id: 'time_to_first_quote_minutes',
            label: 'Time to First Quote (minutes)',
            sf: 'BlockQuote_TimeToFirstQuote__c',
            type: 'Number'
          }, {
            id: 'sla_met',
            label: 'SLA Met (Y/N)',
            sf: 'BlockQuote_SLAMet__c',
            type: 'Checkbox'
          }, {
            id: 'exception_rate',
            label: 'Exception Rate',
            sf: 'BlockQuote_ExceptionRate__c',
            type: 'Percent'
          }, {
            id: 'funnel_stage',
            label: 'Funnel Stage (Quoted/Hold/Contract)',
            sf: 'BlockQuote_FunnelStage__c',
            type: 'Picklist'
          }].map(f => {
            const enabled = kpiFields[f.id as keyof typeof kpiFields];
            return <label key={f.id} className={`flex items-center gap-4 p-4 border ${enabled ? 'border-stone-200 bg-stone-50/50' : 'border-stone-200'} cursor-pointer hover:bg-stone-50 transition-colors`} data-id="element-760">
                  <input type="checkbox" checked={enabled} onChange={e => setKpiFields({
                ...kpiFields,
                [f.id]: e.target.checked
              })} className="w-4 h-4 accent-red-600" data-id="element-761" />
                  <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 items-center" data-id="element-762">
                    <span className="text-sm text-stone-900" data-id="element-763">{f.label}</span>
                    <span className="font-mono text-xs text-stone-500" data-id="element-764">
                      {f.sf}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-stone-400" data-id="element-765">
                      {f.type}
                    </span>
                  </div>
                </label>;
          })}
          </div>
        </div>
      </div>

      <div className="bg-stone-900 text-stone-50 p-5 flex items-start gap-3" data-id="element-766">
        <Target className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-767" />
        <div data-id="element-768">
          <p className="font-medium text-sm text-stone-50 mb-1" data-id="element-769">
            Executive reporting, without you owning the CRM.
          </p>
          <p className="text-xs text-stone-400 leading-relaxed" data-id="element-770">
            KPIs computed by BlockQuote land directly on Opportunities. Build
            any Salesforce report or dashboard on top of them — no extra ETL, no
            spreadsheets.
          </p>
        </div>
      </div>
    </div>;
}
function Toggle({
  on,
  onChange
}: {
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return <button onClick={() => onChange(!on)} className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${on ? 'bg-red-600' : 'bg-stone-300'}`} aria-pressed={on} data-id="element-771">
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-772" />
    </button>;
}
// ────────────────────────────────────────────────
// E) Webhooks
// ────────────────────────────────────────────────
function Webhooks() {
  const [showSecret, setShowSecret] = useState(false);
  const [events, setEvents] = useState({
    request_created: true,
    first_quote_sent: true,
    hold_requested: true,
    hold_approved: true,
    contracted: false
  });
  const secret = 'whsec_8f2a91cb4e9d3f1a2e5b7c0d8a6f4e2b';
  return <div className="space-y-6" data-id="element-773">
      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-774">
        <div className="p-6 border-b border-stone-100" data-id="element-775">
          <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-776">
            Generic Webhook
          </h2>
          <p className="text-sm text-stone-500" data-id="element-777">
            Deliver BlockQuote events to any HTTPS endpoint. Use this for
            HubSpot, Pipedrive, or internal middleware.
          </p>
        </div>
        <div className="p-6 space-y-5" data-id="element-778">
          <div data-id="element-779">
            <label className="font-mono text-xs uppercase tracking-widest text-stone-500 block mb-2" data-id="element-780">
              Endpoint URL
            </label>
            <input type="text" defaultValue="https://hooks.lomas.com/blockquote/incoming" className="w-full px-3 py-2 border border-stone-300 font-mono text-sm text-stone-900 focus:outline-none focus:border-red-600" data-id="element-781" />
          </div>
          <div data-id="element-782">
            <label className="font-mono text-xs uppercase tracking-widest text-stone-500 block mb-2" data-id="element-783">
              Signing Secret
            </label>
            <div className="flex gap-2" data-id="element-784">
              <div className="flex-1 flex items-center px-3 py-2 border border-stone-300 bg-stone-50 font-mono text-sm" data-id="element-785">
                {showSecret ? secret : '•'.repeat(secret.length)}
              </div>
              <button onClick={() => setShowSecret(!showSecret)} className="px-3 border border-stone-300 hover:bg-stone-50 text-stone-700" aria-label={showSecret ? 'Hide secret' : 'Show secret'} data-id="element-786">
                {showSecret ? <EyeOff className="w-4 h-4" data-id="element-787" /> : <Eye className="w-4 h-4" data-id="element-788" />}
              </button>
              <button className="px-3 border border-stone-300 hover:bg-stone-50 text-stone-700" aria-label="Copy" data-id="element-789">
                <Copy className="w-4 h-4" data-id="element-790" />
              </button>
            </div>
            <p className="text-xs text-stone-500 mt-2" data-id="element-791">
              Verify every request by checking the{' '}
              <span className="font-mono" data-id="element-792">X-BlockQuote-Signature</span> header
              against this secret.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-793">
        <div className="p-6 border-b border-stone-100" data-id="element-794">
          <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-795">Events</h2>
          <p className="text-sm text-stone-500" data-id="element-796">
            Select which BlockQuote events should be delivered.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-797">
          {[{
          id: 'request_created',
          label: 'Request Created',
          desc: 'A new inbound RFP has been received.'
        }, {
          id: 'first_quote_sent',
          label: 'First Quote Sent',
          desc: 'The first quote has shipped to the agency.'
        }, {
          id: 'hold_requested',
          label: 'Hold Requested',
          desc: 'Agency has requested an inventory hold.'
        }, {
          id: 'hold_approved',
          label: 'Hold Approved',
          desc: 'Resort has confirmed the hold.'
        }, {
          id: 'contracted',
          label: 'Contracted',
          desc: 'Deal has moved to contract signed.'
        }].map(e => {
          const on = events[e.id as keyof typeof events];
          return <label key={e.id} className="flex items-start gap-3 p-4 border border-stone-200 cursor-pointer hover:bg-stone-50 transition-colors" data-id="element-798">
                <input type="checkbox" checked={on} onChange={ev => setEvents({
              ...events,
              [e.id]: ev.target.checked
            })} className="w-4 h-4 mt-0.5 accent-red-600" data-id="element-799" />
                <div data-id="element-800">
                  <p className="font-mono text-sm text-stone-900" data-id="element-801">{e.id}</p>
                  <p className="text-xs text-stone-500 mt-0.5" data-id="element-802">{e.desc}</p>
                </div>
              </label>;
        })}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-stone-900 text-stone-50 p-5" data-id="element-803">
        <p className="text-sm text-stone-400" data-id="element-804">
          Send a test payload to verify your endpoint is reachable and signing
          correctly.
        </p>
        <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-sm font-medium text-white transition-colors inline-flex items-center gap-2" data-id="element-805">
          <Zap className="w-4 h-4" data-id="element-806" /> Send test webhook
        </button>
      </div>
    </div>;
}
// ────────────────────────────────────────────────
// F) Health & Logs
// ────────────────────────────────────────────────
function Health() {
  const stats = [{
    label: 'Last successful sync',
    val: '2m ago',
    tone: 'text-emerald-600'
  }, {
    label: 'Last error',
    val: '3d ago',
    tone: 'text-stone-700'
  }, {
    label: 'Success rate (24h)',
    val: '99.4%',
    tone: 'text-emerald-600'
  }, {
    label: 'Sync events (24h)',
    val: '1,284',
    tone: 'text-stone-900'
  }];
  const logs = [{
    ts: '14:32:18',
    evt: 'salesforce_sync_success',
    deal: 'BQ-8492',
    detail: 'status → Hold Placed',
    ok: true
  }, {
    ts: '14:31:54',
    evt: 'salesforce_sync_success',
    deal: 'BQ-8491',
    detail: 'status → Approved',
    ok: true
  }, {
    ts: '14:28:02',
    evt: 'salesforce_sync_success',
    deal: 'BQ-8490',
    detail: 'status → Fully Booked',
    ok: true
  }, {
    ts: '14:24:11',
    evt: 'salesforce_sync_retry',
    deal: 'BQ-8488',
    detail: '429 throttled, retry in 8s',
    ok: false,
    warn: true
  }, {
    ts: '14:24:19',
    evt: 'salesforce_sync_success',
    deal: 'BQ-8488',
    detail: 'status → Hold Placed (retry)',
    ok: true
  }, {
    ts: '14:18:44',
    evt: 'webhook_delivery_success',
    deal: 'BQ-8487',
    detail: 'event: hold_requested',
    ok: true
  }, {
    ts: '11:02:30',
    evt: 'salesforce_sync_failure',
    deal: 'BQ-8462',
    detail: 'INVALID_FIELD: BlockQuote_Tier__c',
    ok: false
  }, {
    ts: '10:58:11',
    evt: 'salesforce_sync_success',
    deal: 'BQ-8461',
    detail: 'status → Approved',
    ok: true
  }, {
    ts: '10:42:09',
    evt: 'salesforce_sync_success',
    deal: 'BQ-8460',
    detail: 'status → Draft',
    ok: true
  }, {
    ts: '09:31:02',
    evt: 'oauth_token_refresh',
    deal: '—',
    detail: 'Refresh token rotated',
    ok: true
  }];
  return <div className="space-y-6" data-id="element-807">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-808">
        {stats.map((s, i) => <div key={i} className="bg-white border border-stone-200 shadow-sm p-5" data-id="element-809">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-810">
              {s.label}
            </p>
            <p className={`font-mono text-2xl ${s.tone}`} data-id="element-811">{s.val}</p>
          </div>)}
      </div>

      <div className="bg-white border border-stone-200 shadow-sm" data-id="element-812">
        <div className="p-6 border-b border-stone-100 flex items-center justify-between" data-id="element-813">
          <div data-id="element-814">
            <h2 className="font-serif text-xl text-stone-900 mb-1" data-id="element-815">Sync Log</h2>
            <p className="text-sm text-stone-500" data-id="element-816">
              Last 20 events. Use Resync to retry the last 24 hours of failed
              deals.
            </p>
          </div>
          <button className="px-4 py-2 border border-stone-300 hover:bg-stone-50 text-sm font-medium text-stone-700 inline-flex items-center gap-2 transition-colors" data-id="element-817">
            <RefreshCw className="w-4 h-4" data-id="element-818" /> Resync last 24h
          </button>
        </div>
        <div className="divide-y divide-stone-100" data-id="element-819">
          {logs.map((l, i) => <div key={i} className="px-6 py-3 grid grid-cols-[80px_24px_180px_100px_1fr] gap-4 items-center text-sm" data-id="element-820">
              <span className="font-mono text-xs text-stone-500" data-id="element-821">{l.ts}</span>
              {l.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-822" /> : l.warn ? <AlertCircle className="w-4 h-4 text-amber-600" data-id="element-823" /> : <XCircle className="w-4 h-4 text-red-600" data-id="element-824" />}
              <span className="font-mono text-xs text-stone-700" data-id="element-825">{l.evt}</span>
              <span className="font-mono text-xs text-stone-500" data-id="element-826">{l.deal}</span>
              <span className="text-xs text-stone-600" data-id="element-827">{l.detail}</span>
            </div>)}
        </div>
      </div>

      <div className="bg-stone-900 text-stone-50 p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-828">
        <div className="flex items-start gap-3" data-id="element-829">
          <Shield className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-830" />
          <p className="text-xs text-stone-400 leading-relaxed" data-id="element-831">
            <span className="text-stone-50 font-medium" data-id="element-832">Fail closed.</span> If
            Salesforce is unreachable, BlockQuote still works. Failed syncs
            retry with exponential backoff.
          </p>
        </div>
        <div className="flex items-start gap-3" data-id="element-833">
          <Database className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-834" />
          <p className="text-xs text-stone-400 leading-relaxed" data-id="element-835">
            <span className="text-stone-50 font-medium" data-id="element-836">
              Idempotent by design.
            </span>{' '}
            Deal ID is the external key. Retries never duplicate rows.
          </p>
        </div>
        <div className="flex items-start gap-3" data-id="element-837">
          <Lock className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-838" />
          <p className="text-xs text-stone-400 leading-relaxed" data-id="element-839">
            <span className="text-stone-50 font-medium" data-id="element-840">Tenant isolated.</span>{' '}
            Sync logs are scoped to your resort org. OAuth tokens are encrypted
            at rest.
          </p>
        </div>
      </div>
    </div>;
}